// ONE CONTRACTOR'S RATE, ON THE TICKED ISSUES.
//
// Owner, 2026-10-22: "a selective override per contractor for bulk checked
// issues".
//
// The rate itself is not new: the contractor hours and cost page has set a
// rate on many lines at once since 2026-10-15, and the endpoint it uses
// (PUT /billable-hours/item-overrides/bulk) already takes a list of
// { system, ref, contractorCompanyId } with a rate, checks every line is on
// this account, caps the batch and writes one override per item+contractor.
// Nothing server-side was missing. What was missing is that the Issues
// Manager - where people actually pick the issues - could not reach it.
//
// WHY IT NAMES A CONTRACTOR AND WILL NOT GUESS. An item override is keyed by
// item AND contractor, because two trades can both log hours on one issue at
// different rates. Picking the contractor for you would mean picking one of
// those and silently repricing the other's time, so the field is required and
// has no default.
//
// Its own file for the hooks lint's path budget - see
// feedback_hooks_lint_path_limit - and because a modal with four fields does
// not belong inside a 2,600-line page.
import React, { useState } from "react";
import { Modal, Select, InputNumber, Input, Radio, Alert, Space } from "antd";
import { bulkSetItemOverrides } from "api/billableHours";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import { tradesText, usePartnerTrades } from "components/Partners/partnerTrades";

export default function BulkContractorRateModal({
  open, onClose, onDone, issueIds, contractors, projectId, count,
}) {
  // its own trades lookup rather than one threaded through the page: it is
  // cached per project for a minute, and the page does not hold one
  const trades = usePartnerTrades(projectId || null);
  const [companyId, setCompanyId] = useState(null);
  const [rate, setRate] = useState(null);
  const [appliesTo, setAppliesTo] = useState("owner");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const chosen = (contractors || []).find((c) => c.companyId === companyId) || null;
  const n = issueIds ? issueIds.length : count || 0;

  const save = async () => {
    setError("");
    if (!companyId) { setError("Pick the contractor whose hours this rate prices."); return; }
    if (!(Number(rate) >= 0)) { setError("Give an hourly rate."); return; }
    setBusy(true);
    try {
      const r = await bulkSetItemOverrides({
        items: (issueIds || []).map((id) => ({
          system: "issues",
          ref: String(id),
          contractorCompanyId: companyId,
          contractorName: (chosen && chosen.companyName) || "",
        })),
        rate: Number(rate),
        appliesTo,
        reason: reason.trim(),
      });
      onDone && onDone(r, { companyName: (chosen && chosen.companyName) || "", rate: Number(rate), count: n });
      onClose();
    } catch (e) {
      // THE SERVER'S OWN WORDS. It refuses a line that is not on this account,
      // or a batch over its cap, and says which - repeating that verbatim is
      // more use than "could not save".
      setError((e && (e.err || e.message)) || "Could not set the rate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={busy ? undefined : onClose}
      onOk={save}
      okText={`Set the rate on ${n} issue${n === 1 ? "" : "s"}`}
      confirmLoading={busy}
      title="One contractor's rate, on the ticked issues"
      destroyOnClose
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <div style={{ color: "#6B7C86", fontSize: 12.5 }}>
          This prices that contractor&apos;s hours on these issues, in the cost report
          and the exports. It does not change what the ledger recorded, and it
          leaves every other contractor&apos;s time on the same issues alone.
        </div>

        <div>
          <div style={{ fontSize: 12.5, marginBottom: 4 }}>Contractor</div>
          <Select
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="search"
            optionLabelProp="text"
            placeholder="Whose hours does this rate price?"
            aria-label="Contractor for the rate"
            value={companyId || undefined}
            onChange={setCompanyId}
            options={(contractors || []).map((c) => {
              const name = c.companyName || "(unnamed)";
              const t = (trades && trades.tradesOf(c.companyId)) || [];
              return {
                value: c.companyId,
                text: name,
                search: `${name} ${tradesText(t)}`,
                label: t.length ? <PartnerWithTrades name={name} trades={t} /> : name,
              };
            })}
          />
        </div>

        <div>
          <div style={{ fontSize: 12.5, marginBottom: 4 }}>Hourly rate</div>
          <InputNumber
            style={{ width: 160 }}
            min={0}
            max={10000}
            step={5}
            precision={2}
            placeholder="e.g. 85"
            aria-label="Hourly rate"
            value={rate}
            onChange={setRate}
          />
        </div>

        <div>
          <div style={{ fontSize: 12.5, marginBottom: 4 }}>Who sees this price</div>
          {/* A RADIO AND NEVER A DEFAULT THAT SHOWS OTHER PEOPLE MONEY: the
              cost page makes the same choice the same way. */}
          <Radio.Group value={appliesTo} onChange={(e) => setAppliesTo(e.target.value)}>
            <Space direction="vertical" size={2}>
              <Radio value="owner">Your reports only</Radio>
              <Radio value="everyone">Their reports and digests too</Radio>
            </Space>
          </Radio.Group>
        </div>

        <Input
          placeholder="Why (optional) - shown beside the override"
          aria-label="Reason"
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        {error ? <Alert type="error" showIcon message={error} /> : null}
      </Space>
    </Modal>
  );
}
